// src/components/practices/MagicMoney.js
// Magic Money — gratitude for money, support, possessions, opportunities, and experiences
// already received throughout life. NOT The Money Magnet (that one reframes a specific bill or
// expense); this one is a broader reflection. Per spec ("create its Practice card and basic
// entry experience... we can refine the full interaction later") this is deliberately the
// simplest interactive Practice so far — a single prompt, no secondary fields, closer in shape
// to Magic Rock than even The Money Magnet's two-field screen.
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import PageHeader from '../ui/PageHeader'
import PrimaryButton from '../ui/PrimaryButton'
import PracticeThankYouTap from './shared/PracticeThankYouTap'
import PracticeComplete from './shared/PracticeComplete'
import './shared/PracticeHeading.css'

const REREAD_MS = 2000

export default function MagicMoney({ practice, session, city, onComplete, onBack, augustTheme, onAugustThemeChange }) {
  // Starts on 'practice' directly — the detail screen (AugustPracticeDetail) already shows this
  // Practice's tagline + description before "Begin Practice" is tapped; a separate in-flow intro
  // phase repeating that same copy under a redundant "Begin Practice" title was an extra tap
  // with no new information, not a real step (never counted toward any step total).
  const [phase, setPhase] = useState('practice') // 'practice' | 'complete'
  const [gratitude, setGratitude] = useState('')
  const [thanks, setThanks] = useState(0)
  const [reread, setReread] = useState(false)
  const [saving, setSaving] = useState(false)
  const rereadTimer = useRef(null)

  useEffect(() => () => clearTimeout(rereadTimer.current), [])

  const typed = gratitude.trim().length > 2
  const isReady = typed && thanks >= 3
  const eyebrow = `Day ${practice.unlockDay} · ${practice.name}`

  const tapThanks = () => {
    if (thanks >= 3) return
    const next = thanks + 1
    setThanks(next)
    if (next < 3) {
      setReread(true)
      clearTimeout(rereadTimer.current)
      rereadTimer.current = setTimeout(() => setReread(false), REREAD_MS)
    }
  }

  const handleFinish = async () => {
    if (!isReady || saving) return
    setSaving(true)
    if (session) {
      await supabase.from('practice_completions').insert({
        user_id: session.user.id,
        city: city || 'august',
        practice_id: practice.id,
        payload: { gratitude },
      })
    }
    setSaving(false)
    setPhase('complete')
  }

  if (phase === 'practice') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="What Have You Received?"
          showBack onBack={onBack}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <p className="practice-question">
            What are you grateful for when it comes to money, support, or things you've already received?
          </p>
          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="I'm grateful for…"
              value={gratitude}
              onChange={e => setGratitude(e.target.value)}
            />
          </div>

          <PracticeThankYouTap
            count={thanks}
            reread={reread}
            canTap={typed}
            onTap={tapThanks}
            writtenText={gratitude}
          />

          <PrimaryButton onClick={isReady ? handleFinish : undefined} disabled={!isReady || saving}>
            <span>Done</span>
            <span>✦</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  // phase === 'complete'
  return (
    <PracticeComplete
      headline="Practice Complete"
      subline="You noticed the abundance already here."
      onDone={onComplete}
    />
  )
}
